import { Flex } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { throwNotFoundError } from '~/components/ResourceErrorBoundary'
import { useDebounce } from '~/hooks/useDebounce'
import { CallDetailsView } from '~/pages/Calls/CallDetails/CallDetailsView'
import { CallTableColumn } from '~/pages/Calls/data/CallTableData'
import { useCall } from '~/queries/callQueries'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import {
  DateRange,
  SearchValue,
  SelectedFilterData,
  SortDirection,
} from '~/types/Shared'

import { TCallAnalyticsTab } from './CallDetails/CallDetailsAnalyticsContent'
import {
  CallsViewModelProvider,
  useCallsViewModelContext,
} from './Calls.viewmodel'
import { CallsListView } from './CallsListView'

const DEBOUNCE_WAIT_MS = 500
const END_REACHED_COOLDOWN_MS = 500

export const CallsPage = () => {
  return (
    <CallsViewModelProvider>
      <CallsView />
    </CallsViewModelProvider>
  )
}
export const CallsView = () => {
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState<SearchValue>()
  const [scrollEndReached, setScrollEndReached] = useState(false)
  const { state, actions } = useCallsViewModelContext()

  const handleCallSelected = (
    callId: number,
    _initialTab?: TCallAnalyticsTab,
  ) => {
    actions.navigateToCallDetails(callId, _initialTab)
  }

  const handleFilterDataChanged = (activeFilterData: SelectedFilterData[]) => {
    actions.setSelectedFilterData(activeFilterData)
  }

  const handleDateTimeRangeChanged = (dateTimeRange: DateRange) => {
    actions.setDateTimeRange(dateTimeRange)
  }

  const handleEndReached = () => {
    if (scrollEndReached) return

    setScrollEndReached(true)
    actions.tryGetNextCallPage()
    setTimeout(() => {
      setScrollEndReached(false)
    }, END_REACHED_COOLDOWN_MS)
  }

  const handleSortValueChanged = (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => {
    //get the opposite sort direction
    const inverseSortDirection =
      currentSortDirection === SortDirection.Ascending
        ? SortDirection.Descending
        : SortDirection.Ascending

    actions.setColumnOrdering(columnType, inverseSortDirection)
  }

  //when search changes, requery calls
  const [, resetDebounce] = useDebounce(
    () => {
      actions.setSearchValue(debouncedSearchValue)
    },
    DEBOUNCE_WAIT_MS,
    [debouncedSearchValue],
  )

  const handleSearchValueChanged = (value?: SearchValue) => {
    resetDebounce()
    setDebouncedSearchValue(value)
  }

  //on mount, fetch pbx users, fetch calls
  useEffect(() => {
    resetDebounce()
  }, [])

  useEffect(() => {
    if (state.searchValue?.value === debouncedSearchValue?.value) return

    setDebouncedSearchValue(state.searchValue)
    resetDebounce()
  }, [state.searchValue])

  return (
    <>
      <Flex
        flexDirection="row"
        width="100%"
        alignSelf="stretch"
        justifyItems="flex-start"
        alignItems="top"
        position="relative"
      >
        <CallsListView
          calls={state.callsListState?.data}
          searchValue={debouncedSearchValue}
          columnSorting={state.columnSorting}
          dateTimeRange={state.dateTimeRange}
          isLoading={state.isCallsLoading}
          onCallSelected={handleCallSelected}
          onActiveFiltersChanged={handleFilterDataChanged}
          onDateTimeRangeChanged={handleDateTimeRangeChanged}
          onEndReached={handleEndReached}
          onSearchValueChanged={handleSearchValueChanged}
          onSortableColumnClicked={handleSortValueChanged}
        />
        <Outlet />
      </Flex>
    </>
  )
}

export const CallDetailsPage = () => {
  const { t } = useTranslation()
  const { state } = useCallsViewModelContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { callId } = useParams()
  const { data, isLoading, error } = useCall(Number(callId))
  const { pbxId } = useUserState()
  const initialPbxIdRef = useRef(pbxId)

  const handleCallLogDetailClosed = () => {
    navigate(`${ApplicationRoutes.CALLS}?${searchParams.toString()}`)
  }

  useEffect(() => {
    if (initialPbxIdRef.current !== pbxId) {
      handleCallLogDetailClosed()
    }
  }, [pbxId])

  if (!callId) {
    throw new Error('Call ID is required')
  }

  // Only throw error if we're still on the call details page (not navigating away)
  const isOnCallDetailsPage = location.pathname.startsWith(`/calls/${callId}`)
  const is404Error = error && (error as any)?.status === 404
  const shouldShowNotFound =
    isOnCallDetailsPage && !isLoading && (!data || is404Error)

  if (shouldShowNotFound) {
    throwNotFoundError(
      t(
        'calls.detail.notFound',
        'Oops this record has either been deleted or does not exist. If you need help, please contact support.',
      ),
      {
        cause: {
          status: 404,
          statusText: 'Not Found',
        },
      },
    )
  }

  return (
    <CallDetailsView
      callId={Number(callId)}
      callInstance={data}
      callInstanceLoading={isLoading}
      onClosed={handleCallLogDetailClosed}
      initialTab={state.selectedCallInitialTab}
    />
  )
}
